import { Component } from '@theme/component';

const UF_ENDPOINT = 'https://brasilapi.com.br/api/ibge/uf/v1';
const MUNICIPIOS_ENDPOINT = 'https://brasilapi.com.br/api/ibge/municipios/v1/';

/**
 * Multi-step "Go Out Day" signup form: lead capture (etapa 1), calendar
 * reminder via Google/Outlook/Apple/generic (etapa 2), confirmation with
 * invite links (etapa 3). Ported from bolovo-store's
 * components/GoOut/Form/FormIsland.tsx.
 *
 * @typedef {object} GoOutFormRefs
 * @property {HTMLSelectElement} estadoSelect
 * @property {HTMLSelectElement} cidadeSelect
 * @property {HTMLButtonElement} calendarButton
 * @property {HTMLButtonElement} continueButton
 *
 * @extends {Component<GoOutFormRefs>}
 */
export class GoOutFormComponent extends Component {
  requiredRefs = ['estadoSelect', 'cidadeSelect', 'calendarButton', 'continueButton'];

  connectedCallback() {
    super.connectedCallback();
    this.#loadEstados();
  }

  async #loadEstados() {
    const { estadoSelect } = this.refs;
    const placeholder = estadoSelect.querySelector('option');

    try {
      const response = await fetch(UF_ENDPOINT);
      if (!response.ok) return;

      const estados = await response.json();
      estados.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

      for (const estado of estados) {
        const option = document.createElement('option');
        option.value = estado.sigla;
        option.textContent = estado.nome;
        estadoSelect.append(option);
      }
    } catch (error) {
      console.error('Erro ao carregar estados:', error);
    } finally {
      estadoSelect.disabled = false;
      if (placeholder) placeholder.textContent = 'Selecione';
    }
  }

  /** @param {Event} event */
  async handleEstadoChange(event) {
    const { cidadeSelect } = this.refs;
    const sigla = /** @type {HTMLSelectElement} */ (event.target).value;

    cidadeSelect.replaceChildren();

    const placeholder = document.createElement('option');
    placeholder.value = '';
    cidadeSelect.append(placeholder);

    if (!sigla) {
      placeholder.textContent = 'Selecione um estado';
      cidadeSelect.disabled = true;
      return;
    }

    placeholder.textContent = 'Carregando...';
    cidadeSelect.disabled = true;

    try {
      const response = await fetch(`${MUNICIPIOS_ENDPOINT}${sigla}`);
      if (!response.ok) return;

      const cidades = await response.json();

      for (const cidade of cidades) {
        const option = document.createElement('option');
        option.value = cidade.nome;
        option.textContent = cidade.nome;
        cidadeSelect.append(option);
      }
    } catch (error) {
      console.error('Erro ao carregar cidades:', error);
    } finally {
      placeholder.textContent = 'Selecione';
      cidadeSelect.disabled = false;
    }
  }

  /** @param {Event} event */
  handleStep1Submit(event) {
    event.preventDefault();
    // Sem endpoint de destino real no componente original (deco.cx só dava
    // console.log nos dados) — o lead fica só nesse estado local por ora.
    this.dataset.step = '2';
  }

  /** @param {Event} event */
  handleCalendarChange(event) {
    const value = /** @type {HTMLInputElement} */ (event.target).value;
    this.refs.calendarButton.disabled = !value;
  }

  async createCalendarEvent() {
    const selected = this.querySelector('input[name="calendario"]:checked');
    if (!(selected instanceof HTMLInputElement) || !selected.value) return;

    const { calendarButton, continueButton } = this.refs;
    const { activityTitle = '', activityDescription = '', activityAddress = '', activityDuration = '2', activityDatetime = '' } =
      this.dataset;

    calendarButton.disabled = true;
    calendarButton.textContent = 'Criando...';

    try {
      const start = new Date(activityDatetime);
      const end = new Date(start.getTime() + Number(activityDuration) * 60 * 60 * 1000);

      switch (selected.value) {
        case 'google': {
          const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(activityTitle)}&dates=${toUtcStamp(start)}/${toUtcStamp(end)}&details=${encodeURIComponent(activityDescription)}&location=${encodeURIComponent(activityAddress)}`;
          window.open(url, '_blank');
          break;
        }
        case 'outlook': {
          const url = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(activityTitle)}&startdt=${start.toISOString()}&enddt=${end.toISOString()}&body=${encodeURIComponent(activityDescription)}&location=${encodeURIComponent(activityAddress)}`;
          window.open(url, '_blank');
          break;
        }
        case 'apple': {
          downloadIcsFile({ title: activityTitle, description: activityDescription, location: activityAddress, start, end });
          break;
        }
        default: {
          alert(
            `Evento criado!\n\nTítulo: ${activityTitle}\nData: ${start.toLocaleDateString('pt-BR')}\nHorário: ${start.toLocaleTimeString(
              'pt-BR',
              { hour: '2-digit', minute: '2-digit' }
            )}\nLocal: ${activityAddress}\n\nDescrição: ${activityDescription}`
          );
        }
      }

      calendarButton.textContent = 'Evento Criado!';
      continueButton.hidden = false;
    } catch (error) {
      console.error('Erro ao criar evento:', error);
      alert('Erro ao criar evento. Tente novamente.');
      calendarButton.disabled = false;
      calendarButton.textContent = 'Reserve a sua agenda';
    }
  }

  goToStep3() {
    this.dataset.step = '3';
  }
}

/** @param {Date} date */
function toUtcStamp(date) {
  return `${date.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;
}

/**
 * @param {{title: string, description: string, location: string, start: Date, end: Date}} event
 */
function downloadIcsFile({ title, description, location, start, end }) {
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Go Out Day//Event//EN',
    'BEGIN:VEVENT',
    `UID:${start.getTime()}@gooutday.com`,
    `DTSTAMP:${toUtcStamp(new Date())}`,
    `DTSTART:${toUtcStamp(start)}`,
    `DTEND:${toUtcStamp(end)}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${location}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  const blob = new Blob([ics], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = 'go-out-day-event.ics';
  link.click();

  URL.revokeObjectURL(url);
}

customElements.define('go-out-form-component', GoOutFormComponent);
